import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../../context/AuthContext';
import PhotoCropModal from '../../components/ui/PhotoCropModal';
import {
  IconShieldCheck,
  IconCheck,
  IconDroplet,
  IconMail,
  IconArrowRight,
  IconLock,
  IconBuilding,
  IconCamera,
} from '@tabler/icons-react';

const STEPS = [
  { num: 1, label: 'Confirm details' },
  { num: 2, label: 'Supplier details' },
  { num: 3, label: 'Connect Outlook' },
];

export default function FuelManagerOnboarding() {
  const { userProfile, orgId, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1
  const [firstName, setFirstName] = useState(userProfile?.name?.split(' ')[0] || '');
  const [lastName, setLastName] = useState(userProfile?.name?.split(' ').slice(1).join(' ') || '');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [photoURL, setPhotoURL] = useState(userProfile?.photoURL || null);
  const [showCropModal, setShowCropModal] = useState(false);

  // Step 2
  const [suppliers, setSuppliers] = useState([
    { branch: 'All branches', supplierName: '', supplierEmail: '', accountNumber: '' }
  ]);
  const [perBranch, setPerBranch] = useState(false);

  const handleNext = () => setStep(s => s + 1);
  const handleBack = () => setStep(s => s - 1);

  const handlePhotoSave = async (blob) => {
    try {
      const storageRef = ref(storage, `organisations/${orgId}/avatars/${user.uid}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      setPhotoURL(url);
    } catch (err) {
      console.error('Photo upload error:', err);
      throw err;
    }
  };

  const updateSupplier = (i, field, value) => {
    setSuppliers(prev => prev.map((s, j) => j === i ? { ...s, [field]: value } : s));
  };

  const handleFinish = async () => {
    try {
      await updateDoc(doc(db, 'organisations', orgId), {
        dervSuppliers: suppliers,
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'organisations', orgId, 'users', user.uid), {
        name: `${firstName} ${lastName}`.trim(),
        jobTitle,
        phone,
        photoURL: photoURL || null,
        onboardingComplete: true,
        updatedAt: serverTimestamp(),
      });
      navigate('/fuel-dashboard');
    } catch (err) {
      console.error(err);
    }
  };

  const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw', fontFamily: 'inherit', overflow: 'hidden' }}>

      {/* Left panel */}
      <div style={{ width: '28%', background: '#1D9E75', padding: '40px 32px', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40 }}>
          <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconShieldCheck size={18} color="white" stroke={1.5} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 400, color: '#fff' }}>Veritas</div>
        </div>

        <div style={{ flex: 1 }}>
          {STEPS.map((s, i) => {
            const isDone = step > s.num;
            const isActive = step === s.num;
            return (
              <div key={s.num}>
                <div style={{ display: 'flex', gap: 12, paddingBottom: 20 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 24, flexShrink: 0 }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: isDone || isActive ? '#fff' : 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: isDone || isActive ? '#1D9E75' : 'rgba(255,255,255,0.5)', flexShrink: 0 }}>
                      {isDone ? <IconCheck size={13} stroke={2} color="#1D9E75" /> : s.num}
                    </div>
                    {i < STEPS.length - 1 && <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.2)', marginTop: 4 }} />}
                  </div>
                  <div style={{ flex: 1, paddingTop: 3 }}>
                    <div style={{ fontSize: 13, color: isDone ? 'rgba(255,255,255,0.6)' : isActive ? '#fff' : 'rgba(255,255,255,0.4)' }}>{s.label}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
          An <span style={{ color: 'rgba(255,255,255,0.5)' }}>AGRIWARE</span> product &copy; 2026
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, background: 'var(--color-bg-tertiary)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: '48px 64px' }}>
          <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', letterSpacing: '0.05em', marginBottom: 8 }}>STEP {step} OF {STEPS.length}</div>

          {/* ── Step 1 ── */}
          {step === 1 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Welcome, {firstName || 'there'}</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 28, lineHeight: 1.6 }}>
                Confirm your details before we get your supplier information set up.
              </p>

              <div style={{ display: 'flex', gap: 10, marginBottom: 28 }}>
                <div style={{ background: '#E1F5EE', border: '0.5px solid #5DCAA5', borderRadius: 'var(--radius-md)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#085041' }}>
                  <IconDroplet size={14} stroke={1.5} />
                  Fuel manager · All branches
                </div>
                <div style={{ background: 'var(--color-bg-secondary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-md)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--color-text-secondary)' }}>
                  <IconLock size={11} stroke={1.5} color="var(--color-text-tertiary)" />
                  Role locked
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 48, alignItems: 'start' }}>

                {/* Avatar */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <div
                    onClick={() => setShowCropModal(true)}
                    style={{ width: 100, height: 100, borderRadius: '50%', background: photoURL ? 'transparent' : '#E1F5EE', backgroundImage: photoURL ? `url(${photoURL})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: '#085041', cursor: 'pointer', position: 'relative', overflow: 'hidden', border: '2px solid transparent', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#1D9E75'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
                  >
                    {!photoURL && initials}
                    <div
                      style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s', borderRadius: '50%' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.35)'; e.currentTarget.querySelector('svg') && (e.currentTarget.querySelector('svg').style.opacity = '1'); }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0)'; e.currentTarget.querySelector('svg') && (e.currentTarget.querySelector('svg').style.opacity = '0'); }}
                    >
                      <IconCamera size={22} color="white" stroke={1.5} style={{ opacity: 0, transition: 'opacity 0.2s' }} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span onClick={() => setShowCropModal(true)} style={{ fontSize: 12, color: '#1D9E75', cursor: 'pointer' }}>
                      {photoURL ? 'Change' : 'Add photo'}
                    </span>
                    {photoURL && (
                      <>
                        <span style={{ color: 'var(--color-text-tertiary)' }}>·</span>
                        <span onClick={() => setPhotoURL(null)} style={{ fontSize: 12, color: 'var(--color-text-tertiary)', cursor: 'pointer' }}>Remove</span>
                      </>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textAlign: 'center', maxWidth: 100 }}>JPG, PNG or WebP</div>
                </div>

                {/* Fields */}
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>First name</label>
                      <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Last name</label>
                      <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Job title</label>
                    <input type="text" placeholder="e.g. Fleet Manager, Operations Manager" value={jobTitle} onChange={e => setJobTitle(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Phone number</label>
                    <input type="tel" placeholder="+44 7700 900 000" value={phone} onChange={e => setPhone(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', background: '#fff' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Work email</label>
                    <input type="email" value={userProfile?.email || ''} readOnly style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)' }} />
                    <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Fuel order emails will be sent from this address via your connected Outlook</div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Step 2 ── */}
          {step === 2 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Fuel supplier details</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 12, lineHeight: 1.6 }}>
                Add your Derv supplier contact. When a tank drops below its order threshold, Veritas will automatically send an order email from your Outlook to this address.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 18px', marginBottom: 24, maxWidth: 560 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 400, marginBottom: 2 }}>Different supplier per branch?</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Turn this on if different branches use different fuel suppliers</div>
                </div>
                <div
                  onClick={() => {
                    setPerBranch(!perBranch);
                    if (!perBranch) {
                      setSuppliers([
                        { branch: 'Aylesbury', supplierName: '', supplierEmail: '', accountNumber: '' },
                        { branch: 'Milton Keynes', supplierName: '', supplierEmail: '', accountNumber: '' },
                        { branch: 'Buckingham', supplierName: '', supplierEmail: '', accountNumber: '' },
                        { branch: 'Bicester', supplierName: '', supplierEmail: '', accountNumber: '' },
                      ]);
                    } else {
                      setSuppliers([{ branch: 'All branches', supplierName: '', supplierEmail: '', accountNumber: '' }]);
                    }
                  }}
                  style={{ width: 44, height: 24, borderRadius: 12, background: perBranch ? '#1D9E75' : 'var(--color-bg-secondary)', border: `0.5px solid ${perBranch ? '#1D9E75' : 'var(--color-border-medium)'}`, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                >
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: perBranch ? 22 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>
                {suppliers.map((supplier, i) => (
                  <div key={i} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '20px 22px' }}>
                    {perBranch && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                        <IconBuilding size={16} color="#1D9E75" stroke={1.5} />
                        <div style={{ fontSize: 14, fontWeight: 400 }}>{supplier.branch}</div>
                      </div>
                    )}
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Supplier contact name</label>
                      <input type="text" placeholder="e.g. Paul Brown" value={supplier.supplierName} onChange={e => updateSupplier(i, 'supplierName', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Supplier email address</label>
                      <input type="email" placeholder="e.g. paul.brown@ukfuels.co.uk" value={supplier.supplierEmail} onChange={e => updateSupplier(i, 'supplierEmail', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
                      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Order emails will be sent to this address from your Outlook</div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Account number</label>
                      <input type="text" placeholder="e.g. GB-48291" value={supplier.accountNumber} onChange={e => updateSupplier(i, 'accountNumber', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
                      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Included in every order email so the supplier can identify your account</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── Step 3 ── */}
          {step === 3 && (
            <>
              <h1 style={{ fontSize: 28, fontWeight: 400, marginBottom: 6 }}>Connect Outlook</h1>
              <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 16, lineHeight: 1.6 }}>
                Connect your Microsoft Outlook so Veritas can send Derv order emails directly from your inbox. Your supplier will receive orders from your email address and can reply directly to you.
              </p>

              <div style={{ background: '#E1F5EE', border: '0.5px solid #5DCAA5', borderRadius: 'var(--radius-lg)', padding: '14px 18px', display: 'flex', gap: 12, marginBottom: 28, maxWidth: 560 }}>
                <IconDroplet size={20} color="#1D9E75" stroke={1.5} style={{ flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 400, color: '#085041', marginBottom: 4 }}>Required for automatic Derv ordering</div>
                  <div style={{ fontSize: 12, color: '#0F6E56', lineHeight: 1.6 }}>
                    Order emails are sent from your Outlook so your supplier can reply directly to you with confirmations and PO numbers. Without Outlook connected, automatic ordering is disabled.
                  </div>
                </div>
              </div>

              <div style={{ background: '#E6F1FB', border: '0.5px solid #B5D4F4', borderRadius: 'var(--radius-lg)', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, maxWidth: 480 }}>
                <div style={{ width: 48, height: 48, background: '#0078D4', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <IconMail size={24} color="white" stroke={1.5} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 400, color: '#185FA5', marginBottom: 3 }}>Microsoft Outlook</div>
                  <div style={{ fontSize: 12, color: '#185FA5', opacity: 0.8 }}>Connect your work Outlook account via Microsoft 365</div>
                </div>
              </div>

              <button style={{ background: '#0078D4', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '12px 24px', fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <IconMail size={16} stroke={1.5} /> Connect Microsoft Outlook
              </button>

              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 8, maxWidth: 480 }}>
                You can connect Outlook later from your profile settings, but automatic Derv ordering won't work until it's connected.
              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div style={{ borderTop: '0.5px solid var(--color-border-light)', padding: '16px 64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', flexShrink: 0 }}>
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Step {step} of {STEPS.length}</span>
          <div style={{ display: 'flex', gap: 10 }}>
            {step > 1 && (
              <button onClick={handleBack} style={{ background: 'transparent', color: 'var(--color-text-secondary)', border: '0.5px solid var(--color-border-medium)', borderRadius: 'var(--radius-md)', padding: '10px 20px', fontSize: 13, cursor: 'pointer' }}>Back</button>
            )}
            {step < STEPS.length ? (
              <button onClick={handleNext} style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 24px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                Next <IconArrowRight size={15} stroke={1.5} />
              </button>
            ) : (
              <button onClick={handleFinish} style={{ background: '#1D9E75', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 24px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                <IconCheck size={15} stroke={1.5} /> Go to dashboard
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Photo crop modal */}
      {showCropModal && (
        <PhotoCropModal
          onSave={handlePhotoSave}
          onClose={() => setShowCropModal(false)}
        />
      )}

    </div>
  );
}